import { Box, Grid2 } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import CustomTable from '@ui-components/Table/CustomTable';
import CustomTableBody from '@ui-components/Table/CustomTableBody';
import CustomTableCell from '@ui-components/Table/CustomTableCell';
import CustomTableHead from '@ui-components/Table/CustomTableHead';
import CustomTableRow from '@ui-components/Table/CustomTableRow';
import CustomTooltip from '@ui-components/ToolTip/CustomToolTip';
import CustomLink from '@ui-components/Link/CustomLink';
import CustomPagination from '@ui-components/Table/CustomPagination';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePastSession } from '../../API/hooks/consultMoreDetailsHook';
import VectorIcon from '@assets/images/Vector.svg';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import moment from 'moment';

const PastSession: React.FC = () => {
  const location = useLocation();
  const [consultId] = useState(location.state?.consultId ?? 0);
  const userId = Number(localStorage.getItem('userId') ?? '0');
  const navigate = useNavigate();
  const {
    PastSessionList,
    isLoading,
    refetch: refetchPastSession,
  } = usePastSession(userId, consultId);
  const [currentPage, setCurrentPage] = useState(1);
  const hasFetched = useRef(false);

  const rowsPerPage = 5;
  const PastSessionLength = PastSessionList?.pastSessionRecords.length ?? 0;
  // Calculate rows to display based on pagination
  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return PastSessionList?.pastSessionRecords.slice(
      startIndex,
      startIndex + rowsPerPage
    );
  }, [currentPage, rowsPerPage, PastSessionList?.pastSessionRecords]);

  useEffect(() => {
    if (hasFetched.current) return;

    hasFetched.current = true;

    refetchPastSession();
  }, []);

  // Function to handle page change
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <Box className="mt-16 text-align-center">
        <CustomTypography id="past-session-loading" variant="h6">
          Loading...
        </CustomTypography>
      </Box>
    );
  }

  return (
    <Box className="mt-16">
      <Grid2 container>
        <Grid2 size={{ xs: 12 }}>
          {paginatedRows && paginatedRows?.length > 0 ? (
            <>
              <CustomTable id="pastSession-table">
                <CustomTableHead id="pastSession-table-head">
                  <CustomTableRow id="pastSession-table-row">
                    <CustomTableCell
                      id="pastSession-table-cell-1"
                      className="past-session-date-column"
                    >
                      Consult Date
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-2"
                      align="right"
                    >
                      Consulting Provider
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-3"
                      align="right"
                    >
                      Diagnosis
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-4"
                      align="right"
                    >
                      Recommendation
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-5"
                      align="right"
                    >
                      Status
                    </CustomTableCell>
                    <CustomTableCell
                      id="pastSession-table-cell-6"
                      align="right"
                    >
                      Actions
                    </CustomTableCell>
                  </CustomTableRow>
                </CustomTableHead>
                <CustomTableBody id="pastSession-table-body">
                  {paginatedRows?.map((row) => (
                    <CustomTableRow
                      key={row.consultId}
                      id="pastSession-table-cell-1"
                    >
                      <CustomTableCell
                        id="pastSession-table-cell-2"
                        className="ellipsis-cell"
                      >
                        <CustomTooltip
                          id="pastSession-table-tooltip"
                          title={
                            row.createdOn
                              ? moment
                                  .utc(row.createdOn)
                                  .local()
                                  .format('MMM D, YYYY - HH:mm')
                              : 'N/A'
                          }
                          placement="top"
                        >
                          <span className="ellipsis-cell">
                            {row.createdOn
                              ? moment
                                  .utc(row.createdOn)
                                  .local()
                                  .format('MMM D, YYYY - HH:mm')
                              : 'N/A'}
                          </span>
                        </CustomTooltip>
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-3"
                        align="right"
                      >
                        {row.providerName ? `Dr. ${row.providerName}` : 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-4"
                        align="right"
                      >
                        {row.diagnosis ? row.diagnosis : 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-5"
                        align="right"
                      >
                        {row.recommendation ? row.recommendation : 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-6"
                        align="right"
                      >
                        {row.sessionStatus ? row.sessionStatus : 'N/A'}
                      </CustomTableCell>
                      <CustomTableCell
                        id="pastSession-table-cell-6"
                        align="right"
                      >
                        <Box
                          className="flex text-align-left item-center"
                          gap={0.5}
                        >
                          <CustomLink
                            color="primary"
                            underline="hover"
                            variant="body3"
                            onClick={() => {
                              if (row.consultId !== consultId) {
                                navigate('/provider/details', {
                                  state: {
                                    consultId: row.consultId,
                                    prevCaseId: consultId,
                                    assignedFlag: location.state?.assignedFlag,
                                  },
                                  // replace: true,
                                });
                              }
                            }}
                            id="moredetailsLink"
                          >
                            <span className="display-inline-flex align-items-center cursor-pointer">
                              More Details&nbsp;
                              <VectorIcon />
                            </span>
                          </CustomLink>
                        </Box>
                      </CustomTableCell>
                    </CustomTableRow>
                  ))}
                </CustomTableBody>
              </CustomTable>

              <Box display="flex" justifyContent="center" mt={2}>
                <CustomPagination
                  id="past-session-pagination"
                  count={Math.ceil(PastSessionLength / rowsPerPage)} // Calculate the total number of pages
                  page={currentPage}
                  onPageChange={handlePageChange}
                  color="primary"
                  siblingCount={1}
                  boundaryCount={1}
                  showFirstButton={true}
                  showLastButton={true}
                  size="small"
                />
              </Box>
            </>
          ) : (
            <Grid2
              size={{ xs: 12, sm: 12, md: 6, lg: 4 }}
              className="button-width"
            >
              <Box className="text-align-center no-consults-data">
                <CustomTypography
                  variant="body2"
                  color="textDisabled"
                  id="noAcceptedConsults"
                >
                  No past consults available
                </CustomTypography>
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default PastSession;
